import { Component, createRef } from 'preact'
import { ProductUpdateType } from './Types'
import UserAvatar from '../User/UserAvatar'
import Phrase from '../../libs/translationsUtils/phrase'
import Content from '../common/Content'
import Axios from 'axios'
import LikedUsers, { LikedUserType } from '../topic-list-view/LikedUsers'
import Editor from '../editor'
import TopicPublicTags from '../topic-list-view/TopicPublicTags'
import { common } from '../common'
import { canUserEditPost } from '../utils/permissions'
import { CONTENT_TYPE_PRODUCT_UPDATE } from '@insided/shared/utils/contentType'
import Tooltip from '../../control-components/ui-components/Tooltip'
import Portal from 'preact-portal'
import PostActions from '../content/post-actions.sync'
import classNames from 'classnames'
import formatDateInLocalTimezone from '../utils/dateFormatDestination'
import { PageUnloadContext } from '@insided/shared/services/PageUnloadContext'

interface ProductUpdateTopicViewProps {
  topic: ProductUpdateType
  currentUser?: LikedUserType
  currentPage: number
  isPreview?: boolean
  isSubscribed?: boolean
}

interface ProductUpdateTopicViewState {
  content: string | null
  isCurrentUserLiked: boolean
  tags: string[]
  likedUsers: {
    [key: string]: LikedUserType
  }
  showEditor: boolean
  isLoading: boolean
  hideFirstPost: boolean
  isSubscribed: boolean
  editorErrors: Array<{
    category: string
    phrase: string
  }>
}

class ProductUpdateTopicView extends Component<ProductUpdateTopicViewProps, ProductUpdateTopicViewState> {
  private readonly canUserEditPost: boolean = canUserEditPost() && !this.props.isPreview
  private readonly isProductAreasEnabled: boolean = common.isModuleActive('productAreas')

  constructor (props: ProductUpdateTopicViewProps) {
    super(props)
    this.state = {
      content: props.topic.content,
      isCurrentUserLiked: !!this.props.currentUser && this.props.currentUser.id !== null && this.props.topic.likedBy?.includes(this.props.currentUser.id.toString()),
      likedUsers: {},
      showEditor: false,
      isLoading: false,
      editorErrors: [],
      hideFirstPost: props.currentPage > 1,
      tags: props.topic.tags,
      isSubscribed: !!props.isSubscribed
    }
    this.handleLikeStatusToggle = this.handleLikeStatusToggle.bind(this)
    this.handleSubscriptionToggle = this.handleSubscriptionToggle.bind(this)
    this.handleOpeningPostChange = this.handleOpeningPostChange.bind(this)
    this.onChange = this.onChange.bind(this)
    this.onCancel = this.onCancel.bind(this)
  }

  content = createRef()
  // This content ref is responsible for storing the previous value of the post when we edit topic.
  // onChange method keeps updating the state and when we cancel the edit the content uses the value from state
  // which is changed. We can also set the value we receive from props but this is received from twig, and it is
  // not refreshed until we refresh the browser.

  async handleSubscriptionToggle () {
    if (!this.props.currentUser) return

    if (this.props.currentUser.id === null) {
      const event = new CustomEvent('GuestUserAction')
      window.dispatchEvent(event)
      return
    }
    const endpoint = `/product-updates/${this.props.topic.id}/${this.state.isSubscribed ? 'unsubscribe' : 'subscribe'}`
    await Axios.post(endpoint)

    this.setState({ isSubscribed: !this.state.isSubscribed })
  }

  async handleLikeStatusToggle () {
    if (!this.props.currentUser) return

    if (this.state.isCurrentUserLiked) {
      delete this.state.likedUsers[this.props.currentUser.id]
      this.setState({
        likedUsers: this.state.likedUsers,
        isCurrentUserLiked: false
      })
    } else {
      this.setState({
        likedUsers: {
          ...this.state.likedUsers,
          [this.props.currentUser.id]: this.props.currentUser
        },
        isCurrentUserLiked: true
      })
    }
  }

  async handleOpeningPostChange () {
    this.setState({ isLoading: true })
    try {
      await Axios.post(`/community/api/productUpdates/${this.props.topic.id}/editContent`, {
        content: this.state.content
      })
      this.setState(state => {
        this.content.current = state.content
        return { showEditor: false, content: state.content, isLoading: false }
      })
    } catch (e) {
      this.setState({ isLoading: false })
      if (e.response?.data?.errors?.content) {
        this.setState({ editorErrors: [{ category: 'Forum', phrase: 'validation.text.required' }] })
      } else {
        this.setState({ editorErrors: [{ category: 'Forum', phrase: 'Something\'s gone wrong' }] })
      }
    }
  }

  async componentDidMount () {
    if (this.props.topic.likedBy?.length) {
      const res = await Axios.post('/member/getUsersById', {
        ids: this.props.topic.likedBy ?? []
      })

      this.setState({ likedUsers: res.data })
    }
    this.content.current = this.props.topic.content
  }

  onChange (content: string) {
    this.setState({ content })
  }

  onCancel () {
    this.setState({ showEditor: false, content: this.content.current })
  }

  render () {
    const { topic, currentPage, isPreview } = this.props
    const { content, isCurrentUserLiked, likedUsers, tags, showEditor, isLoading, editorErrors, hideFirstPost } = this.state
    const { author, publishedAt, content: data } = topic
    return (
      <div>
        <Portal into='.list-item--publish-date'>
          <Tooltip
            withArrow
            trigger={(
              <time> {common.relativeDate(publishedAt)} </time>
              )}
            tooltipMessage={(
              <div className={classNames('tooltip__content', 'tooltip__content--publish-date', 'js-tooltip-content', 'position--bottom')}>
                {formatDateInLocalTimezone(new Date(publishedAt))}
              </div>
              )}
          />
        </Portal>
        <div
          className={classNames(
          'post',
            'box__pad',
            'qa-topic-post-box',
            'post--odd js-toggle-target',
            `${hideFirstPost ? 'is-hidden' : ''}`
          )}
        >
          <div className='post__user__info'>
            <UserAvatar
              user={{ ...author, ...{ name: author.username } }}
              extraClass='avatar avatar--responsive'
              showRank
            />
          </div>
          <div className='post__user__meta'>
            <ul className={classNames('list', 'list--dot-seperated', 'text--meta')}>
              <li>
                <div className='group--inline'>
                  <a href={`/members/reguser01-${author.id}`}>{author.username}</a>
                </div>
              </li>
              <li className='is-hidden-S'>
                <Phrase p='topic.meta.post.reply' count={{ n: author.reputation.repliesMade.toString() }} />
              </li>
            </ul>
          </div>
          <div>
            {
              showEditor
                ? (
                  <PageUnloadContext.Consumer>
                    {({ lock, unlock }) => {
                      return (
                        <div className='post__content post__edit'>
                          <Editor
                            data={this.state.content}
                            attachments={[]}
                            canHavePoll={false}
                            isLoading={isLoading}
                            errors={editorErrors}
                            onSubmit={async () => {
                              unlock()
                              await this.handleOpeningPostChange()
                            }}
                            onCancel={() => {
                              unlock()
                              this.onCancel()
                            }}
                            onChange={(content: string) => {
                              lock(content, data)
                              this.onChange(content)
                            }}
                            profile='openingPost'
                          />
                        </div>
                      )
                    }}
                  </PageUnloadContext.Consumer>
                  )
                : (
                  <Content
                    content={content ?? ''}
                    className={classNames('post__content', 'qa-topic-post-content', 'post__content--new-editor')}
                  />
                  )
            }
          </div>
          {
            this.isProductAreasEnabled
              ? (
                <TopicPublicTags
                  topicId={topic.id}
                  contentType={CONTENT_TYPE_PRODUCT_UPDATE}
                  tags={tags}
                  canUserEdit={this.canUserEditPost}
                  onChange={(newTags) => this.setState({ tags: newTags })}
                />
                )
              : null
          }
          {
              !isPreview && Object.keys(likedUsers).length
                ? (
                  <LikedUsers likedUsers={Object.keys(likedUsers).map(id => likedUsers[id])} previewCount={3} isCurrentUserLiked={isCurrentUserLiked} />
                  )
                : null
            }
          {
            !isPreview
              ? (
                <div className='post__footer'>
                  <PostActions
                    privateId={parseInt(topic.id)}
                    topicId={parseInt(topic.publicId)}
                    contentType={CONTENT_TYPE_PRODUCT_UPDATE}
                    onEditClick={() => this.setState({ showEditor: true })}
                    isLiked={isCurrentUserLiked}
                    onLikeClick={this.handleLikeStatusToggle}
                    showLike
                    showReport={false}
                    showBestAnswer={false}
                    quoteText={topic.content}
                    quoteUsername={topic.author.username}
                    showEdit={this.canUserEditPost}
                  />
                </div>
                )
              : null
          }
        </div>
        {
          currentPage > 1
            ? (
              <a href='#' className='first-post__close' onClick={() => this.setState({ hideFirstPost: !hideFirstPost })}>
                {
                hideFirstPost ? <Phrase p='showFirstpost.message' /> : <Phrase p='hideFirstpost.message' />
              }
              </a>
              )
            : null
        }
      </div>

    )
  }
}
export default ProductUpdateTopicView
